import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './ActivationLogModal.module.css';

type ActivationLog = {
  id: number;
  gameName: string;
  gameId: string;
  cdk: string;
  steamUserId: string;
  price: number;
  proxyName: string;
  updateTime: string;
};

const PERIOD_LABEL: Record<number, string> = {
  1: '今日',
  2: '昨日',
  3: '近七天',
  4: '本月',
  5: '上月',
};

const AVATAR_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

const colorOfName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = hash * 31 + name.charCodeAt(i);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: number;
  nested?: boolean;
};

export const ActivationLogModal = ({ open, onOpenChange, period, nested }: Props) => {
  const [logs, setLogs] = useState<ActivationLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPage<ActivationLog>(
        `/admin/tel/activation/logs?period=${period}&limit=100`
      );
      setLogs(result.records);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, fetchLogs]);

  const label = PERIOD_LABEL[period] ?? '';

  return (
    <Modal
      nested={nested}
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>{label}激活记录</div>
          <div className={styles.bannerSub}>共 {total} 条激活记录</div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : logs.length > 0 ? (
          <div className={styles.list}>
            {logs.map((log) => (
              <div key={log.id} className={styles.item}>
                <span
                  className={styles.avatar}
                  style={{ background: colorOfName(log.gameName || log.gameId || '') }}
                >
                  {firstChineseChar(log.gameName || log.gameId || '')}
                </span>
                <div className={styles.itemMain}>
                  <div className={styles.itemTop}>
                    <span className={styles.name}>{log.gameName || log.gameId}</span>
                    <span className={styles.time}>{log.updateTime}</span>
                  </div>
                  <div className={styles.itemBottom}>
                    <span className={styles.proxy}>{log.proxyName || '—'}</span>
                    <span className={styles.cdk}>{log.cdk}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.empty}>暂无激活记录</div>
        )}
      </div>
    </Modal>
  );
};
